<template>
  <div class="payments-section mt-2">
    <p class="payments-section-title px-2">Payment history</p>
    <div class="payments-card">
      <div v-for="(item,index) in paymentItems" :key="index" class="payment-row">
        <div class="payment-icon">
          <v-icon size="18" color="success">mdi-credit-card-check-outline</v-icon>
        </div>

        <div class="payment-copy">
          <p class="payment-title">{{ item.cardLabel }}</p>
          <p class="payment-meta">{{ item.paidAtLabel }}</p>
        </div>

        <div class="payment-amount">
          <p class="payment-total">{{ item.amountText }}</p>
          <div class="payment-status">
            <span class="status-dot" :class="`status-dot--${item.statusKey}`" />
            <span class="status-label">{{ item.statusLabel }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

type SubscriptionDetailResponse = {
  payments?: {
    amount: number | string | null;
    status: string | null;
    card_brand: string | null;
    card_last4: string | null;
    paid_at: string | null;
  }[];
};

const props = defineProps<{
  detail: SubscriptionDetailResponse;
}>();

function titleCase(value: string | null | undefined) {
  const text = String(value ?? "").trim();
  if (!text) return "Card";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function formatPaidAt(value: string | null) {
  if (!value) return "-";
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function paymentStatusMeta(status: string | null | undefined) {
  const key = String(status ?? "").trim().toLowerCase();
  if (key === "succeeded" || key === "success") return { key: "succeeded", label: "Succeeded" };
  if (key === "failed") return { key: "failed", label: "Failed" };
  return { key: "succeeded", label: titleCase(key || "succeeded") };
}

function formatAmount(amount: number | string | null) {
  const value = Number(amount ?? 0);
  if (Number.isNaN(value)) return "$0.00 USD";
  return `$${value.toFixed(2)} USD`;
}

const paymentItems = computed(() =>
  (props.detail.payments ?? []).map((item) => {
    const meta = paymentStatusMeta(item.status);
    const brand = titleCase(item.card_brand);
    return {
      cardBrand: item.card_brand ?? "",
      amountText: formatAmount(item.amount),
      cardLabel: `${brand} •••• ${item.card_last4 || "----"}`,
      paidAtLabel: formatPaidAt(item.paid_at),
      statusKey: meta.key,
      statusLabel: meta.label,
    };
  }),
);
</script>

<style scoped>
.payments-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payments-section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #6b7280;
}

.payments-card {
  padding: 0;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
}

.payment-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-bottom: 1px solid #e5e7eb;
}

.payment-row:last-child {
  border-bottom: 0;
}

.payment-icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(249, 115, 22, 0.12);
  flex: 0 0 auto;
}

.payment-copy {
  min-width: 0;
  flex: 1;
}

.payment-title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.payment-meta {
  margin: 2px 0 0;
  font-size: 14px;
  color: #6b7280;
}

.payment-amount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex: 0 0 auto;
}

.payment-total {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.payment-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #22c55e;
}

.status-dot--succeeded {
  background: #22c55e;
}

.status-dot--failed {
  background: #ef4444;
}

.status-label {
  font-size: 13px;
  font-weight: 400;
  color: #475569;
}
</style>
